import React, { memo, useCallback, useMemo, useEffect } from 'react'

import {
  Icon,
  Button,
  AIGenerated,
  Check,
  classnames,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool, string } from 'prop-types'

import { useLocation, useNavigate } from 'Pods/Shared/ContextBasedRouter'
import DesktopOnlyTooltip from 'Pods/Shared/DesktopOnlyTooltip/DesktopOnlyTooltip'
import useApiPost from 'Pods/Shared/Hooks/useApiPost'
import usePageConfig from 'Pods/Shared/Hooks/usePageConfig'
import { iconSize } from 'Pods/ThreeDStudio/constants'
import { buildGeneratingRunUrl } from 'Pods/ThreeDStudio/Hooks/generatingRunUrl'
import useGenerationConcurrency from 'Pods/ThreeDStudio/Hooks/useGenerationConcurrency'
import useOpenedModelReadOnly from 'Pods/ThreeDStudio/Hooks/useOpenedModelReadOnly'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import { buildGeneratePayload } from 'Pods/ThreeDStudio/Services/buildGeneratePayload'
import { buildOpenedModelFromState }
  from 'Pods/ThreeDStudio/Services/buildOpenedModelFromState/buildOpenedModelFromState'
import {
  savePendingGeneration,
  buildGenerationSnapshot,
  clearPendingGeneration,
} from 'Pods/ThreeDStudio/Services/pendingGenerationStorage'
import PaidFeatureBadge from 'Pods/ThreeDStudio/Shared/PaidFeatureBadge/PaidFeatureBadge'
import { getEnhancedSettingsKey } from 'Pods/ThreeDStudio/Shared/Utils/getEnhancedSettingsKey'
import { isSourceGenerationModel } from 'Pods/ThreeDStudio/Shared/Utils/isSourceGenerationModel'
import { useThreeDStudioStore, threeDStudioStore } from 'Pods/ThreeDStudio/Store'
import {
  addActiveRun,
  updateActiveRun,
  removeActiveRun,
  setHasGenerated,
  setAvailableCredits,
  setHasFailed,
} from 'Pods/ThreeDStudio/Store/actions/generation'
import {
  openSignInModal,
  openUsageModal,
  openPaidFeatureModal,
  clearPendingGenerationTrigger,
  clearPendingPostAuthGenerationTrigger,
  setOpenedModel,
} from 'Pods/ThreeDStudio/Store/actions/ui'
import {
  GENERATION_TRIGGER_KEY,
  MODAL_STUDIO_ACCESS_TIERS as MODAL_TIERS,
  STUDIO_ACCESS_TIER,
} from 'Pods/ThreeDStudio/Store/constants'

import useAreRequiredParamsFulfilled from './Hooks/useAreRequiredParamsFulfilled'
import { centerTooltipOnCrown } from './Utils/centerTooltipOnCrown'

// Tiers where a restored pending intent only refills the form and waits - the post-auth
// replay must NOT auto-click for them. not_subscribed is the post-payment/pre-webhook state
// (auto-clicking would bounce the user back into the upgrade modal they just paid from);
// waitlisted means the user already joined and can only wait, so re-opening the modal each
// return adds nothing. eligible_for_trial and must_join_waitlist are deliberately excluded:
// the replay opens their modal (start the free trial / join the waitlist) so the user can
// act straight away.
const REPLAY_SKIP_TIERS = new Set([
  STUDIO_ACCESS_TIER.NOT_SUBSCRIBED,
  STUDIO_ACCESS_TIER.WAITLISTED,
])

// Correlates an optimistic activeRuns entry with its POST response before the
// backend run id exists.
let clientRunSeq = 0
const nextClientRunId = () => {
  clientRunSeq += 1
  return `client-run-${clientRunSeq}`
}

function GenerateButton({
  generateLabel,
  generatedLabel,
  generatingLabel,
  isEnhancementMode = false,
  isDisabled = false,
  tooltipMessage = undefined,
  withIcon = true,
}) {
  const t = useTranslation()
  const pageConfig = usePageConfig()
  const { isCurrentUser: isLoggedIn } = pageConfig || {}
  const isReadOnly = useOpenedModelReadOnly()

  const areRequiredParamsFulfilled = useAreRequiredParamsFulfilled(isEnhancementMode)

  const { postData, isLoading } = useApiPost()
  const navigate = useNavigate()
  const location = useLocation()
  const activeRuns = useThreeDStudioStore(state => state.generation.activeRuns)
  const openedSessionId = useThreeDStudioStore(state => state.ui.openedModel?.generatorSessionId)
  const { isCapReached, isPaidTier, isMultipleEnabled } = useGenerationConcurrency()
  const availableCredits = useThreeDStudioStore(state => state.generation.availableCredits)
  const activeViewerSidebarTab = useThreeDStudioStore(state => state.ui.activeViewerSidebarTab)
  const hasGenerated = useThreeDStudioStore(state => state.generation.hasGenerated)
  const studioAccessTier = useThreeDStudioStore(state => state.subscription.studioAccessTier)
  const pendingGenerationTrigger = useThreeDStudioStore(state => state.ui.pendingGenerationTrigger)
  const pendingPostAuthGenerationTrigger = useThreeDStudioStore(state => state.ui.pendingPostAuthGenerationTrigger)
  // The model schema needed to build the run payload is recomputed by useActiveModelSchema
  // and is momentarily absent right after a post-auth restore; a model_id means it is ready.
  const activeModelSchemaId = useThreeDStudioStore(state => (
    (isEnhancementMode ? state.schema.enhancedModelSchema : state.schema.generationModelSchema)?.model_id
  ))
  const isModalTier = MODAL_TIERS.has(studioAccessTier)
  const isQuotaHydrating = isLoggedIn === true && studioAccessTier === null

  const triggerKey = isEnhancementMode ? activeViewerSidebarTab : GENERATION_TRIGGER_KEY
  const isOwnRunInFlight = activeRuns.some(run => (
    run.triggeredBy === triggerKey
    && (run.enhancingSessionId == null || String(run.enhancingSessionId) === String(openedSessionId))
  ))
  // Modal tiers never reach the cap (they cannot start runs); their click keeps
  // opening the trial/subscribe modal instead.
  const isCapBlocked = !isModalTier && isCapReached
  // At the concurrency cap, a free/trial user is invited to subscribe (paid tiers
  // get a higher cap): the label gains the paid-feature crown, hovering explains
  // the paid perk, and clicking opens the paid-feature upsell modal (same as the
  // Private mode / DCC / Export locked controls). Deliberately NOT gated on
  // :enable_multiple_generations - with the flag off the cap is the legacy single
  // run, and "another simultaneous generation" is still the paid feature being
  // upsold; gating on the flag left every account outside its rollout with the
  // legacy disabled button and no upsell at all. A capped paid user is told when
  // the action frees up again instead. Only an enhancement button whose own run is
  // in flight keeps its plain in-progress presentation - a "Processing..." label
  // wearing a crown that opens a subscribe modal would be incoherent.
  const showUpsellCrown = isCapBlocked && !isPaidTier && !(isEnhancementMode && isOwnRunInFlight)
  // Enhancement buttons are keyed by their tab; the main/initial generate button
  // uses the GENERATION_TRIGGER_KEY sentinel. Buttons stay enabled while the user
  // is under their tier's concurrency cap; only an enhancement button whose own run
  // (for the opened session) is in flight shows the in-progress label. The main
  // button never does - it goes straight back to "Generate" so another concurrent
  // generation can be started. With :enable_multiple_generations off, paid tiers
  // keep the legacy behavior instead (the trigger of the single in-flight run shows
  // the in-progress label); free tiers get the upsell crown above.
  const isThisButtonGenerating = (isEnhancementMode || !isMultipleEnabled)
    && isOwnRunInFlight
    && !showUpsellCrown

  useEffect(() => {
    if (hasGenerated) {
      setTimeout(() => {
        setHasGenerated(false)
      }, 3000)
    }
  }, [hasGenerated])

  const handleClick = useCallback(() => {
    if (!isLoggedIn) {
      // Account creation finishes with a full reload back to /ai, wiping the store,
      // so stash the form draft + which button was pressed. useResumePendingGeneration
      // restores it on the way back and replays this click to continue the generation.
      if (areRequiredParamsFulfilled) {
        savePendingGeneration({
          triggerKey,
          isEnhancementMode,
          snapshot: buildGenerationSnapshot(threeDStudioStore.getState()),
        })
      }
      openSignInModal('signup')
      return
    }

    // A cap-blocked click on a free/trial tier cannot start a run; it upsells the
    // higher paid cap instead. Paid tiers never get here (their capped button
    // stays disabled with the "frees up" tooltip).
    if (showUpsellCrown) {
      openPaidFeatureModal()
      return
    }

    const state = threeDStudioStore.getState()
    const { subscription, generation } = state
    const { studioAccessTier: currentTier, generationCost } = subscription

    if (currentTier === null || currentTier === undefined) {
      return
    }

    if (MODAL_TIERS.has(currentTier)) {
      // Tiers that can unlock generation from inside the modal: the free trial enrolls
      // in place (rides the in-memory trigger below), the paid subscription redirects to
      // the payment form and back to /ai. Waitlist tiers cannot unlock it, so they get no
      // auto-continue. Only a fulfilled form is worth replaying.
      const isUnlockableTier = currentTier === STUDIO_ACCESS_TIER.ELIGIBLE_FOR_TRIAL
        || currentTier === STUDIO_ACCESS_TIER.NOT_SUBSCRIBED
      const shouldAutoGenerate = isUnlockableTier && areRequiredParamsFulfilled
      openUsageModal(shouldAutoGenerate ? triggerKey : null)

      if (shouldAutoGenerate) {
        // Re-persist so the intent also survives the subscription payment redirect; the
        // free-trial path enrolls in place and never reads this back. Cleared when the
        // generation finally starts or when the user dismisses the modal.
        savePendingGeneration({
          triggerKey,
          isEnhancementMode,
          snapshot: buildGenerationSnapshot(threeDStudioStore.getState()),
        })
      } else {
        clearPendingGeneration()
      }

      return
    }

    if (typeof generationCost !== 'number' || generationCost <= 0) {
      return
    }

    // The wallet is the source of truth for spendability: trial and subscription
    // grants live in wallet buckets, so the credits check below already covers
    // exhausted quotas. Re-gating on monthly/extra counters here falsely blocked
    // users whose trial was drained but who held credits in other buckets.
    const hasEnoughCredits = (generation.availableCredits ?? 0) >= generationCost

    if (!hasEnoughCredits) {
      openUsageModal()
      return
    }

    const currentSidebarTab = state.ui.activeViewerSidebarTab
    const settingsKey = isEnhancementMode
      ? getEnhancedSettingsKey(currentSidebarTab, state.schema.enhancedAction)
      : 'modelSettings'
    const schema = isEnhancementMode
      ? state.schema.enhancedModelSchema
      : state.schema.generationModelSchema
    const additionalPayloadData = (() => {
      if (isEnhancementMode) {
        const openedModelId = state.ui.openedModel?.id
        // Tag the enhancement with the source run whenever the opened model is a base
        // text-to-3d / image-to-3d generation, so the backend records the remix lineage.
        const sourceRunData = openedModelId && isSourceGenerationModel(state.ui.openedModel?.providerModelId)
          ? { source_run_id: openedModelId }
          : {}

        // Opening the uploaded source model from the variations tab resets the
        // generation slice, so fall back to the opened model's session to keep the
        // enhancement attached as a new variation of that session.
        const generatorSessionId = state.generation.generatorSessionId
          || state.ui.openedModel?.generatorSessionId

        // Only the owner can target a model's generator session; the backend rejects a
        // foreign session id, so a non-owned enhancement omits it and starts a fresh
        // session under the current user.
        return !isReadOnly && generatorSessionId
          ? { generator_session_id: generatorSessionId, ...sourceRunData }
          : sourceRunData
      }

      const { isPrivate, selectedUseCase } = state.modelSettings
      // Creating an image in the AI Assistant records the session its conversation lives
      // in, so the model generated next joins that session instead of opening a new one -
      // that is what makes the chat available on the model afterwards (the Create tab
      // restores from the session the opened model belongs to). Guarded on ownership like
      // the enhancement branch above, since the backend rejects a foreign session id.
      const { imageAssistantSessionId } = state.ui

      return {
        private: isPrivate,
        ...(selectedUseCase ? { use_case: selectedUseCase } : {}),
        ...(!isReadOnly && imageAssistantSessionId
          ? { generator_session_id: imageAssistantSessionId }
          : {}),
      }
    })()

    const payload = buildGeneratePayload(settingsKey, schema, additionalPayloadData)
    // Only an enhancement of an owned model that already has a generator session
    // adds a variation in place and drives the "Processing..." overlay on that
    // session's library card. Enhancing a model the user does not own, or generating
    // from a freshly uploaded model (no session yet), starts a fresh session - there
    // is no existing card to overlay, so it shows the loading card like an initial
    // generation instead.
    const enhancingSessionId = isEnhancementMode ? state.ui.openedModel?.generatorSessionId : undefined
    const isOwnedEnhancement = isEnhancementMode && !isReadOnly && enhancingSessionId != null
    // The intent has now produced a run (whether from a normal click or a post-auth /
    // post-subscription replay), so drop any persisted copy to prevent a stale replay.
    clearPendingGeneration()
    const clientId = nextClientRunId()
    addActiveRun({
      clientId,
      triggeredBy: isEnhancementMode ? currentSidebarTab : GENERATION_TRIGGER_KEY,
      isEnhancement: isEnhancementMode,
      enhancingSessionId: isOwnedEnhancement ? enhancingSessionId : undefined,
      startedAt: Date.now(),
    })

    postData({
      path: '/api/internal/generator/runs',
      data: payload,
      onSuccess: (response) => {
        const responseData = response.data.data.attributes
        const { id, generatorSessionId, remainingCredits, estimatedSeconds } = responseData
        updateActiveRun(clientId, { runId: id, sessionId: generatorSessionId, estimatedSeconds })
        const onSuccessState = threeDStudioStore.getState()
        setAvailableCredits(remainingCredits.total_credits)

        // Only an initial generation can hydrate the viewer from local state:
        // buildOpenedModelFromState dispatches on schema.generationModelSchema, so running
        // an enhancement through it would describe the wrong model entirely.
        if (!isEnhancementMode) {
          const lastGenerationSettings = buildOpenedModelFromState(onSuccessState, id)
          updateActiveRun(clientId, { settings: lastGenerationSettings })
          setOpenedModel(lastGenerationSettings)
        }

        // Every run that is not an owned enhancement gets its own "Generating..." card
        // (an owned one overlays its session's existing card instead - see the
        // isOwnedEnhancement comment above), so point the URL at that card. Otherwise
        // the "Generate New 3D Model" card stays highlighted and nothing shows which run
        // is being viewed. Activation keys on the run id alone, which is why this needs
        // none of the settings above. A query parameter, not a path: see
        // buildGeneratingRunUrl for why an in-flight run cannot use the shared-run path.
        if (!isOwnedEnhancement) {
          // A fresh-session enhancement leaves its source model in the viewer - either the
          // opened model's file or an uploaded inputThreeDUrl - and ViewerArea gates the
          // progress banner on both being absent, so the user would watch the source model
          // instead of their generation. Clearing resets modelSettings too, which is what
          // drops the uploaded url. Initial generations replaced openedModel just above,
          // and an owned enhancement is excluded on purpose: it overlays its session's card
          // with "Processing..." and keeps the source model on screen.
          if (isEnhancementMode) {
            setOpenedModel({})
          }

          const runUrl = buildGeneratingRunUrl(id)

          if (`${location?.pathname ?? ''}${location?.search ?? ''}` !== runUrl) {
            navigate(runUrl, { replace: true })
          }
        }
      },
      onError: (error) => {
        // todo add error id when api returns it
        removeActiveRun({ clientId })
        setHasFailed(true)
        const errorMessage = error?.response?.data?.errors?.[0] || t('shared.generateButton.genericError')
        if (typeof window !== 'undefined' && window.showNotification) {
          window.showNotification(errorMessage, { type: 'error', autohide: true })
        }
      },
    })
  }, [
    t,
    navigate,
    location,
    isEnhancementMode,
    isLoggedIn,
    isReadOnly,
    postData,
    triggerKey,
    areRequiredParamsFulfilled,
    showUpsellCrown,
  ])

  // Replays the generation a trial-eligible user asked for before enrolling:
  // once the free-trial enrollment inside the usage modal flips the tier to a
  // generating-capable one, the pending trigger left by handleClick fires the
  // same click path again with the now-unlocked quota.
  useEffect(() => {
    if (
      pendingGenerationTrigger !== triggerKey
      || isModalTier
      || !studioAccessTier
      || !areRequiredParamsFulfilled
    ) {
      return
    }

    // Re-check synchronously: several buttons can share a trigger key (e.g. the
    // error-banner retry renders the main trigger too), and all their effects run
    // in the same commit - only the first one may consume the trigger.
    const { ui, generation } = threeDStudioStore.getState()
    if (ui.pendingGenerationTrigger !== triggerKey || generation.isGenerating) {
      return
    }

    clearPendingGenerationTrigger()
    handleClick()
  }, [
    pendingGenerationTrigger,
    studioAccessTier,
    isModalTier,
    triggerKey,
    areRequiredParamsFulfilled,
    handleClick,
  ])

  // Continues the intent a user left before returning to /ai. useResumePendingGeneration
  // restores their form draft and arms this trigger; the matching button then replays its
  // click - either to start the generation (generating-capable tier) or to open the
  // trial/subscribe modal (eligible_for_trial). It stays hands-off on the restore-only
  // tiers so it never bounces a just-paid user back into the upgrade modal.
  useEffect(() => {
    if (
      pendingPostAuthGenerationTrigger !== triggerKey
      || !isLoggedIn
      || !studioAccessTier
      || !areRequiredParamsFulfilled
      // not_subscribed (post-payment, pre-webhook) and the waitlist tiers only get the form
      // refilled - no auto-click. eligible_for_trial is NOT here, so its click is replayed
      // to open the trial/subscribe modal.
      || REPLAY_SKIP_TIERS.has(studioAccessTier)
      // On a generating-capable tier the replay actually starts a run, so the schema must be
      // ready (areRequiredParamsFulfilled is vacuously true while it is empty) and the wallet
      // balance loaded (else handleClick reads 0 credits and bounces to the usage modal).
      // eligible_for_trial only opens the modal, so it needs neither.
      || (!isModalTier && !activeModelSchemaId)
      || (!isModalTier && availableCredits == null)
    ) {
      return
    }

    // Re-check synchronously: several buttons can share a trigger key, and all their
    // effects run in the same commit - only the first one may consume the trigger.
    const { ui, generation } = threeDStudioStore.getState()
    if (ui.pendingPostAuthGenerationTrigger !== triggerKey || generation.isGenerating) {
      return
    }

    clearPendingPostAuthGenerationTrigger()
    handleClick()

    // A just-started enhancement keeps its source model in the viewer, which hides the
    // "Generating your 3D model" progress banner; clearing the opened model surfaces it,
    // matching what clicking the library's generating card does. Initial generations have
    // no opened model, so the banner already shows and this is a no-op for them.
    const startedGeneration = threeDStudioStore.getState().generation
    if (isEnhancementMode && startedGeneration.isGenerating && startedGeneration.triggeredBy === triggerKey) {
      setOpenedModel({})
    }
  }, [
    pendingPostAuthGenerationTrigger,
    isLoggedIn,
    studioAccessTier,
    isModalTier,
    activeModelSchemaId,
    availableCredits,
    triggerKey,
    isEnhancementMode,
    areRequiredParamsFulfilled,
    handleClick,
  ])

  const label = useMemo(() => {
    if (isThisButtonGenerating) {
      return generatingLabel
    }
    if (hasGenerated) {
      return generatedLabel
    }

    return generateLabel
  }, [isThisButtonGenerating, hasGenerated, generateLabel, generatedLabel, generatingLabel])

  const buttonIcon = useMemo(
    () => <Icon svgComponent={hasGenerated ? Check : AIGenerated} color="light" size={iconSize} notSpaced />,
    [hasGenerated],
  )

  const button = (
    <Button
      {...(withIcon && {
        icon: buttonIcon,
        variant: 'with-icon',
      })}
      type="ghost"
      size="small"
      customClass={
        classnames(
          'generate-area__button cgt-body-base-semibold',
          hasGenerated && 'generate-area__button--generated',
          showUpsellCrown && 'generate-area__button--upsell',
          !withIcon && 'generate-area__button--no-icon'
        )
      }
      onClick={handleClick}
      disabled={
        isQuotaHydrating
        || isThisButtonGenerating
        || hasGenerated
        // The upsell click opens the paid-feature modal rather than starting a run, so
        // no can't-generate-right-now reason applies to it: neither the caller-level
        // isDisabled (upload in flight, incompatible model, tab-specific required
        // params) nor the form/slot checks below.
        || (!showUpsellCrown && (
          isDisabled
          || (!isModalTier && !areRequiredParamsFulfilled)
          || isLoading
          || isCapBlocked
        ))
      }
    >
      {label}
      {showUpsellCrown && (
        <PaidFeatureBadge
          className="generate-area__button-badge"
          label={t('shared.subscribersOnly.simultaneousGenerationsTitle')}
        />
      )}
    </Button>
  )

  const effectiveTooltipMessage = (() => {
    if (isThisButtonGenerating) {
      // The button already shows the in-progress label; no tooltip needed.
      return null
    }
    if (showUpsellCrown) {
      return t('shared.subscribersOnly.simultaneousGenerationsTitle')
    }
    if (isCapBlocked) {
      // Paid tiers: legacy single-run copy while :enable_multiple_generations is
      // off, the frees-up copy with it on.
      return isMultipleEnabled
        ? t('shared.generateButton.limitReachedTooltip')
        : t('shared.generateButton.inProgressTooltip')
    }
    // Fall back to any caller-provided tooltip (e.g. compatibility).
    return tooltipMessage
  })()

  if (!effectiveTooltipMessage) {
    return button
  }

  return (
    <DesktopOnlyTooltip
      content={effectiveTooltipMessage}
      id={`generate-button-tooltip-${triggerKey}`}
      overridePosition={showUpsellCrown ? centerTooltipOnCrown : undefined}
    >
      {button}
    </DesktopOnlyTooltip>
  )
}

GenerateButton.propTypes = {
  generateLabel: string.isRequired,
  generatedLabel: string.isRequired,
  generatingLabel: string.isRequired,
  isEnhancementMode: bool,
  isDisabled: bool,
  tooltipMessage: string,
  withIcon: bool,
}

export default memo(GenerateButton)
